import React, { useEffect, useState, createContext, useContext } from 'react';
// Message type definition
type MessageType = 'text' | 'image' | 'file' | 'notification';
// Message interface
interface Message {
  id: string;
  sender: 'student' | 'teacher' | 'system';
  content: string;
  timestamp: string;
  type?: MessageType;
  attachments?: {
    id: string;
    name: string;
    type: 'image' | 'document' | 'video';
    url: string;
    size?: string;
  }[];
  read?: boolean;
}
// Conversation interface
interface Conversation {
  id: string;
  type: 'direct' | 'group' | 'support' | 'program' | 'notification';
  title: string;
  preview: string;
  status?: 'waiting' | 'processing' | 'completed';
  date: string;
  unread: boolean;
  recipient: {
    id: string;
    name: string;
    role?: string;
    avatar?: string;
  };
  participants?: {
    id: string;
    name: string;
    avatar?: string;
  }[];
  messages: Message[];
}
// Context interface
interface MessagesContextValue {
  conversations: Conversation[];
  addConversation: (conversation: Conversation) => void;
  removeConversation: (id: string) => void;
  addMessageToConversation: (conversationId: string, message: Message) => void;
  markConversationAsRead: (id: string) => void;
  getUnreadCount: () => number;
}
// Create context
const MessagesContext = createContext<MessagesContextValue | undefined>(undefined);
// Initial conversations data
const initialConversations: Conversation[] = [{
  id: '1',
  type: 'direct',
  title: 'Ayşe Öğretmen',
  preview: 'Merhaba Ahmet, ödevini teslim ettiğin için teşekkürler!',
  date: 'Bugün',
  unread: true,
  recipient: {
    id: 'teacher-1',
    name: 'Ayşe Öğretmen',
    role: 'Matematik Öğretmeni',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&q=80'
  },
  messages: [{
    id: 'm1',
    sender: 'teacher',
    content: 'Merhaba Ahmet, nasılsın?',
    timestamp: 'Bugün, 09:30'
  }, {
    id: 'm2',
    sender: 'student',
    content: 'Merhaba hocam, iyiyim teşekkür ederim. Siz nasılsınız?',
    timestamp: 'Bugün, 09:32'
  }, {
    id: 'm3',
    sender: 'teacher',
    content: 'Ben de iyiyim, teşekkür ederim. Ödevini teslim ettiğin için teşekkürler!',
    timestamp: 'Bugün, 09:35'
  }]
}, {
  id: '2',
  type: 'support',
  title: 'Teknik Destek',
  preview: 'Sorununuz çözüldü mü?',
  date: 'Dün',
  unread: false,
  status: 'completed',
  recipient: {
    id: 'support',
    name: 'Teknik Destek',
    role: 'Yardım Masası'
  },
  messages: [{
    id: 'm1',
    sender: 'student',
    content: 'Merhaba, uygulamada bir sorun yaşıyorum. Video izlerken ses gelmiyor.',
    timestamp: 'Dün, 15:10'
  }, {
    id: 'm2',
    sender: 'teacher',
    content: 'Merhaba, sorununuzu anlıyorum. Lütfen uygulamayı kapatıp tekrar açmayı deneyin.',
    timestamp: 'Dün, 15:15'
  }, {
    id: 'm3',
    sender: 'student',
    content: 'Denedim ama hala aynı sorunu yaşıyorum.',
    timestamp: 'Dün, 15:20'
  }, {
    id: 'm4',
    sender: 'teacher',
    content: 'Anladım. Cihazınızın ses ayarlarını kontrol ettiniz mi?',
    timestamp: 'Dün, 15:22'
  }, {
    id: 'm5',
    sender: 'student',
    content: 'Evet, ses açık ve diğer uygulamalarda ses var.',
    timestamp: 'Dün, 15:25'
  }, {
    id: 'm6',
    sender: 'teacher',
    content: 'Sorunu çözmek için uzaktan destek vermemiz gerekebilir. Müsait olduğunuz bir zaman dilimini belirtebilir misiniz?',
    timestamp: 'Dün, 15:30'
  }, {
    id: 'm7',
    sender: 'student',
    content: 'Yarın öğleden sonra müsaitim.',
    timestamp: 'Dün, 15:35'
  }, {
    id: 'm8',
    sender: 'teacher',
    content: "Tamam, yarın saat 14:00'te sizinle iletişime geçeceğiz. Sorununuz çözüldü mü?",
    timestamp: 'Dün, 16:00'
  }]
}, {
  id: '3',
  type: 'program',
  title: 'Haftalık Program Talebiniz',
  preview: 'Talebiniz işleme alındı, en kısa sürede dönüş yapacağız.',
  date: '2 gün önce',
  unread: false,
  status: 'processing',
  recipient: {
    id: 'guidance-service',
    name: 'Rehberlik Servisi',
    role: 'Akademik Danışman'
  },
  messages: [{
    id: 'm1',
    sender: 'student',
    content: 'Merhaba, LGS sınavına hazırlanıyorum ve matematik ile fen bilimleri derslerine ağırlık vermek istiyorum. Bana uygun bir çalışma programı hazırlayabilir misiniz?',
    timestamp: '2 gün önce, 10:15'
  }, {
    id: 'm2',
    sender: 'system',
    content: 'Program talebiniz alındı. En kısa sürede bir danışman sizinle iletişime geçecektir.',
    timestamp: '2 gün önce, 10:15'
  }, {
    id: 'm3',
    sender: 'teacher',
    content: 'Merhaba Ahmet, program talebini aldık. Sana özel bir program hazırlamak için biraz daha bilgiye ihtiyacımız var. Günde kaç saat çalışabiliyorsun?',
    timestamp: '2 gün önce, 14:30'
  }, {
    id: 'm4',
    sender: 'student',
    content: 'Merhaba, hafta içi günde 3-4 saat, hafta sonu ise 6-7 saat çalışabiliyorum.',
    timestamp: '2 gün önce, 15:45'
  }, {
    id: 'm5',
    sender: 'teacher',
    content: 'Teşekkürler. Programını hazırlıyoruz, en geç yarın sana özel hazırlanmış programı paylaşacağız.',
    timestamp: '2 gün önce, 16:00'
  }]
}, {
  id: '4',
  type: 'notification',
  title: 'Sistem Bildirimi',
  preview: 'Yeni bir ödev eklendi: Matematik - Denklemler',
  date: '3 gün önce',
  unread: false,
  recipient: {
    id: 'system',
    name: 'Sistem Bildirimi'
  },
  messages: [{
    id: 'm1',
    sender: 'system',
    content: 'Yeni bir ödev eklendi: Matematik - Denklemler. Son teslim tarihi: 15 Aralık 2023',
    timestamp: '3 gün önce, 09:00'
  }]
}];
// Provider component
export const MessagesProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    const savedConversations = localStorage.getItem('conversations');
    return savedConversations ? JSON.parse(savedConversations) : initialConversations;
  });
  // Save conversations to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('conversations', JSON.stringify(conversations));
  }, [conversations]);
  // Add a new conversation
  const addConversation = (conversation: Conversation) => {
    setConversations(prev => [conversation, ...prev]);
  };
  // Remove a conversation
  const removeConversation = (id: string) => {
    setConversations(prev => prev.filter(conv => conv.id !== id));
  };
  // Add a message to a conversation
  const addMessageToConversation = (conversationId: string, message: Message) => {
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        // Add the message and update preview and date
        const updatedMessages = [...conv.messages, message];
        return {
          ...conv,
          messages: updatedMessages,
          preview: message.content.substring(0, 50) + (message.content.length > 50 ? '...' : ''),
          date: 'Şimdi',
          // If the sender is not the student, mark as unread
          unread: message.sender !== 'student'
        };
      }
      return conv;
    }));
  };
  // Mark a conversation as read
  const markConversationAsRead = (id: string) => {
    setConversations(prev => prev.map(conv => {
      if (conv.id === id && conv.unread) {
        return {
          ...conv,
          unread: false
        };
      }
      return conv;
    }));
  };
  // Get the count of unread conversations
  const getUnreadCount = () => {
    return conversations.filter(conv => conv.unread).length;
  };
  // Create context value
  const value: MessagesContextValue = {
    conversations,
    addConversation,
    removeConversation,
    addMessageToConversation,
    markConversationAsRead,
    getUnreadCount
  };
  return <MessagesContext.Provider value={value} data-id="element-1391">
      {children}
    </MessagesContext.Provider>;
};
// Custom hook to use the messages context
export const useMessages = () => {
  const context = useContext(MessagesContext);
  if (context === undefined) {
    throw new Error('useMessages must be used within a MessagesProvider');
  }
  return context;
};